import {Button, Col, Container, Form, Row, Spinner, Tab, Tabs} from "react-bootstrap";
import styles from "../usermanagement.module.css";
import {Formik} from "formik";
import * as yup from "yup";
import {useContext, useEffect, useRef} from "react";
import {BackendContext} from "../../../context/Backend";
import {useNavigate, useRevalidator} from "react-router";
import {useTranslation} from "react-i18next";

const TriStateCheck = ({name, label, value, onChange}) => {
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) ref.current.indeterminate = value === null;
    }, [value]);

    const handleChange = () => {
        if (value === null) onChange(true);
        else if (value === true) onChange(false);
        else onChange(null);
    };

    return (
        <Form.Check>
            <Form.Check.Input
                id={`tri-${name}`}
                ref={ref}
                type="checkbox"
                checked={value === true}
                onChange={handleChange}
                className={value === null ? styles.triStateActive : undefined}
            />
            <Form.Check.Label htmlFor={`tri-${name}`}>{label}</Form.Check.Label>
        </Form.Check>
    );
};

export const MultiEditForm = ({users}) => {

    const backend = useContext(BackendContext);
    const navigate = useNavigate();
    const {revalidate} = useRevalidator();
    const {t} = useTranslation();

    const schema = yup.object().shape({});

    const handleCancel = () => navigate("/");

    const buildPermissions = (values) => {
        const permissions = {};
        ['uploadDocument', 'deleteDocument', 'createRequest', 'websiteLogin',
         'syncFailed', 'syncPending', 'syncFinished'].forEach(f => {
            if (values[f] !== null) permissions[f] = values[f] ? 1 : 0;
        });
        if (values.creditPoolAccess !== null) {
            permissions.accountTypeId = values.creditPoolAccess ? 2 : 1;
        }
        return permissions;
    };

    return (<Formik
        validationSchema={schema}
        onSubmit={(values) => {
            const usersToUpdate = {
                userIds: Array.from(users),
                permissions: buildPermissions(values)
            };

            backend.api.updateUsers(usersToUpdate, () => {
                navigate("/", {state: {message: t('system.saveSuccessful')}});
                revalidate();
            }, (error) => {
                navigate("/error", backend.api.getErrorState(error));
            });
        }}
        initialValues={{
            uploadDocument: null,
            deleteDocument: null,
            createRequest: null,
            websiteLogin: null,
            syncFailed: null,
            syncPending: null,
            syncFinished: null,
            creditPoolAccess: null
        }}
    >
        {({handleSubmit, values, setFieldValue, isSubmitting, dirty, isValid}) => (
            <Form className={"p-0 bg-light-subtle " + styles.tabs} onSubmit={handleSubmit} autoComplete={"off"}>
                <Row>
                    <Tabs
                        defaultActiveKey="permissions"
                        transition={true}
                        id="edit-tab"
                        className={"mb-0 bg-primary-subtle"}
                    >
                        <Tab eventKey="permissions" title={t('permissions.header')} className={"small"}>
                            <Container fluid={true} className={"py-3 bg-light-subtle"}>
                                <Row className={"bg-light-subtle mb-3"}>
                                    <Col>
                                        <TriStateCheck name={"uploadDocument"} label={t('permissions.uploadDocument')}
                                                       value={values.uploadDocument} onChange={v => setFieldValue("uploadDocument", v)}/>
                                        <TriStateCheck name={"deleteDocument"} label={t('permissions.deleteDocument')}
                                                       value={values.deleteDocument} onChange={v => setFieldValue("deleteDocument", v)}/>
                                        <TriStateCheck name={"createRequest"} label={t('permissions.createSignatureRequest')}
                                                       value={values.createRequest} onChange={v => setFieldValue("createRequest", v)}/>
                                        <TriStateCheck name={"websiteLogin"} label={t('permissions.websiteLogin')}
                                                       value={values.websiteLogin} onChange={v => setFieldValue("websiteLogin", v)}/>
                                        <TriStateCheck name={"creditPoolAccess"} label={t('permissions.directCredit')}
                                                       value={values.creditPoolAccess} onChange={v => setFieldValue("creditPoolAccess", v)}/>
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                        <Tab eventKey="synchronisation" title={t('synchronisation.header')} className={"small"}>
                            <Container fluid={true} className={"py-3 bg-light-subtle"}>
                                <Row className={"bg-light-subtle"}>
                                    <Col>
                                        <TriStateCheck name={"syncFailed"} label={t('synchronisation.syncFailed')}
                                                       value={values.syncFailed} onChange={v => setFieldValue("syncFailed", v)}/>
                                        <TriStateCheck name={"syncPending"} label={t('synchronisation.syncPending')}
                                                       value={values.syncPending} onChange={v => setFieldValue("syncPending", v)}/>
                                        <TriStateCheck name={"syncFinished"} label={t('synchronisation.syncFinished')}
                                                       value={values.syncFinished} onChange={v => setFieldValue("syncFinished", v)}/>
                                    </Col>
                                </Row>
                            </Container>
                        </Tab>
                    </Tabs>
                </Row>
                <Row>
                    <div className="d-flex gap-2 p-2 justify-content-center">
                        <Button className={"websobutton"} size={"md"} variant={"danger"} onClick={handleCancel}>{t('system.cancelButton')}</Button>
                        <Button className={"websobutton"} type={"submit"} disabled={isSubmitting || !dirty || !isValid} size={"md"}>
                            {isSubmitting ? (<Spinner className={"me-2"} as="span" animation="border" size="sm"/>) : null}
                            {t('system.saveButton')}
                        </Button>
                    </div>
                </Row>
            </Form>
        )}
    </Formik>);
}
